import { CommonModule } from '@angular/common';
import {
  Component,
  effect,
  inject,
  OnDestroy,
  OnInit,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { ERROR_CODES, ERROR_CODES_MESSAGES } from '@kaspacom/wallet-messages';
import { KcNumberInputComponent, KcButtonComponent, NotificationService } from '@kaspacom/ui-kit';
import { AddressResolutionResult } from '../../../../../../../../../../services/address-resolution.service';
import { AssetsManagerService } from '../../../../../../../../../../services/assets-manager/assets-manager.service';
import { L1_ASSET_KEYS } from '../../../../../../../../../../services/assets-manager/assets-stores/l1-assets-store.service';
import { KaspaNetworkActionsService } from '../../../../../../../../../../services/kaspa-netwrok-services/kaspa-network-actions.service';
import { Krc20WalletActionService } from '../../../../../../../../../../services/protocols/krc20/krc20-wallet-actions.service';
import { QrScannerService } from '../../../../../../../../../../services/qr-scanner.service';
import { UtilsHelper } from '../../../../../../../../../../services/utils.service';
import { WalletActionService } from '../../../../../../../../../../services/wallet-action.service';
import { WalletService } from '../../../../../../../../../../services/wallet.service';
import { ApprovalFlowService } from '../../../../../../../../../services/approval-flow.service';
import { AddressSmartInputComponent } from '../../../../../../../../../shared/ui/input/address-smart-input/address-smart-input.component';
import { CheckboxInputComponent } from '../../../../../../../../../shared/ui/input/checkbox/checkbox-input/checkbox-input.component';
import { FlowPageBaseComponent } from '../../../../../../../common/flow-page/base/flow-page-base.component';
import { IFlowPageConfig } from '../../../../../../../common/flow-page/interfaces/flow-page.interface';
import { IToken } from '../../../../../../../common/interfaces/token.interface';
import { Krc20TokenLogoComponent } from '../../../../../../../home/assets-lists/l1/logo/krc20-token-logo/krc20-token-logo.component';

@Component({
  selector: 'app-send-krc20',
  standalone: true,
  imports: [
    CommonModule,
    KcNumberInputComponent,
    CheckboxInputComponent,
    KcButtonComponent,
    FormsModule,
    Krc20TokenLogoComponent,
    AddressSmartInputComponent,
  ],
  templateUrl: './send-krc20.component.html',
  styleUrl: './send-krc20.component.scss',
})
export class SendKrc20Component
  extends FlowPageBaseComponent
  implements OnInit, OnDestroy
{
  private walletService = inject(WalletService);
  private walletActionService = inject(WalletActionService);
  private krc20WalletActionService = inject(Krc20WalletActionService);
  private utilsHelper = inject(UtilsHelper);
  private notificationService = inject(NotificationService);
  private approvalFlowService = inject(ApprovalFlowService);
  private kaspaNetworkActionsService = inject(KaspaNetworkActionsService);
  private qrScannerService = inject(QrScannerService);
  private router = inject(Router);
  private assetsManagerService = inject(AssetsManagerService);

  // Token data
  token = signal<IToken | undefined>(undefined);
  loading = signal<boolean>(true);

  // Form data
  walletAddress: string = '';
  tokenAmount: string = '';
  replaceByFee: boolean = false;

  // Resolved address (from KNS) if present
  private resolvedToAddress: string | null = null;

  // Loading state
  isLoading = false;

  // Track if we're waiting for approval flow completion
  private waitingForApprovalCompletion = false;

  // Validation states
  isAddressValid = true;
  isAmountValid = true;
  addressErrorMessage = '';
  amountErrorMessage = '';

  constructor() {
    super();

    // Effect to watch for approval flow completion
    effect(() => {
      const completion = this.approvalFlowService.completion();
      if (completion && this.waitingForApprovalCompletion) {
        this.waitingForApprovalCompletion = false;

        if (completion.success) {
          // Transaction was successful, navigate back
          this.notificationService.success('Success',
            'KRC20 token sent successfully!',
          );
          this.navigateBack();
        }
        // Error cases are handled by the approval flow itself
      }
    });

    // React to page configuration changes
    effect(() => {
      const currentPage = this.flowPagesService.activePage();
      if (currentPage?.id === 'send-krc20') {
        this.loadTokenData();
      }
    });
  }

  override ngOnInit() {
    // Remove effects from here since they're now in constructor
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();
    this.qrScannerService.stopScanning();
  }

  get config(): IFlowPageConfig {
    return {
      id: 'send-krc20',
      title: `Send ${this.token()?.symbol || 'Token'}`,
      canNavigateBack: true,
    };
  }

  get availableBalance(): number {
    return this.token()?.balance || 0;
  }

  get isFormValid(): boolean {
    return (
      this.isAddressValid &&
      this.isAmountValid &&
      !!this.walletAddress &&
      !!this.tokenAmount &&
      Number(this.tokenAmount) > 0 &&
      !this.isLoading
    );
  }

  private async loadTokenData() {
    const tokenData = this.flowPagesService.activePage()?.data?.[
      'token'
    ] as IToken;

    if (tokenData) {
      // First try to get updated data from assets store
      const krc20Assets = this.assetsManagerService
        .getAllAssetStores()
        .l1.getAssets(L1_ASSET_KEYS.krc20);
      const storedToken = krc20Assets.find((t) => t.tick === tokenData.address);

      if (storedToken) {
        this.token.set({
          name: storedToken.tick,
          symbol: storedToken.tick.toUpperCase(),
          address: storedToken.tick,
          balance: storedToken.balance,
          priceKas: 0.0,
        });
        this.loading.set(false);
      } else {
        // Fallback to the passed token data
        this.token.set(tokenData);
        this.loading.set(false);
      }
    } else {
      // No token data passed, can't proceed
      this.loading.set(false);
      this.notificationService.error('Error', 'No token selected');
      this.navigateBack();
    }
  }

  onWalletAddressChange(address: string): void {
    this.walletAddress = address;
    this.validateAddress();
  }

  onAddressResolved(result: AddressResolutionResult): void {
    // Do not overwrite input value; keep domain text if any
    if (result.effectiveAddress) {
      this.resolvedToAddress = result.effectiveAddress;
      this.isAddressValid = true;
      this.addressErrorMessage = '';
    } else if (result.source === 'kns' && result.error) {
      this.resolvedToAddress = null;
      this.isAddressValid = false;
      this.addressErrorMessage = result.error;
    } else {
      this.resolvedToAddress = null;
      this.validateAddress();
    }
  }

  onAmountChange(amount: any): void {
    this.tokenAmount = amount?.toString() || '';
    this.validateAmount();
  }

  onRbfChange(rbf: boolean): void {
    this.replaceByFee = rbf;
  }

  onMaxAmountClick(): void {
    const maxAmount = this.availableBalance;
    this.tokenAmount = maxAmount > 0 ? maxAmount.toString() : '';
    this.validateAmount();
  }

  onQrScanClick(): void {
    if (this.qrScannerService.isCurrentlyScanning()) {
      this.qrScannerService.stopScanning();
    } else {
      this.qrScannerService.startScanning({
        scannerId: 'qr-scanner-krc20',
        title: 'Scan KRC20 Address',
        onSuccess: (address: string) => {
          this.walletAddress = address;
          this.validateAddress();
        },
        onError: (error: string) => {
          console.error('QR scanning error:', error);
        },
      });
    }
  }

  private validateAddress(): void {
    if (!this.walletAddress || this.walletAddress.trim() === '') {
      this.isAddressValid = false;
      this.addressErrorMessage = 'Address is required';
      return;
    }

    if (
      this.utilsHelper.isValidWalletAddress(this.walletAddress) ||
      !!this.resolvedToAddress
    ) {
      this.isAddressValid = true;
      this.addressErrorMessage = '';
      return;
    }

    this.isAddressValid = false;
    this.addressErrorMessage = 'Invalid wallet address';
  }

  private validateAmount(): void {
    const amount = Number(this.tokenAmount);

    if (!this.tokenAmount || !Number.isFinite(amount) || amount <= 0) {
      this.isAmountValid = false;
      this.amountErrorMessage = 'Amount must be greater than 0';
      return;
    }

    if (amount > this.availableBalance) {
      this.isAmountValid = false;
      this.amountErrorMessage = 'Insufficient balance';
      return;
    }

    this.isAmountValid = true;
    this.amountErrorMessage = '';
  }

  async onSendClick(): Promise<void> {
    if (!this.isFormValid || !this.token()) {
      return;
    }

    const currentWallet = this.walletService.getCurrentWallet();
    if (!currentWallet) {
      this.notificationService.error('Error', 'No wallet selected');
      return;
    }

    this.isLoading = true;

    try {
      // Convert token amount to BigInt using kaspaToSompiFromNumber (same as working sendAsset function)
      const amountInSompi =
        this.kaspaNetworkActionsService.kaspaToSompiFromNumber(
          Number(this.tokenAmount),
        );
      const toAddress = this.resolvedToAddress || this.walletAddress;

      // Create KRC20 transfer action
      const action = this.krc20WalletActionService.createTransferWalletAction(
        this.token()!.address, // ticker
        toAddress, // to address
        amountInSompi, // amount in sompi format
      );

      console.log(
        'KRC20 Transfer Action:',
        action,
        currentWallet,
        this.tokenAmount,
      );

      const result =
        await this.walletActionService.validateAndDoActionAfterApproval(
          action,
          false,
        );

      if (result.success) {
        // Clear form on success
        this.walletAddress = '';
        this.tokenAmount = '';
        this.replaceByFee = false;
        this.resolvedToAddress = null;

        // Only show success message and navigate if not using v2 flow
        // v2 flow handles success display in the approval flow
        if (!result.isUsingV2Flow) {
          this.notificationService.success('Success',
            'KRC20 token sent successfully!',
          );
          this.navigateBack();
        } else {
          // For v2 flow, wait for approval flow completion
          this.waitingForApprovalCompletion = true;
        }
      } else {
        if (result.errorCode !== ERROR_CODES.EIP1193.USER_REJECTED) {
          const errorMessage = result.errorCode
            ? ERROR_CODES_MESSAGES[result.errorCode]
            : ERROR_CODES_MESSAGES[ERROR_CODES.GENERAL.UNKNOWN_ERROR];
          this.notificationService.error('Error', errorMessage);
        }

        // Reset the waiting flag if transaction failed
        this.waitingForApprovalCompletion = false;
      }
    } catch (error) {
      console.error('Error sending KRC20 token:', error);
      this.notificationService.error('Error', 'Failed to send KRC20 token');
      this.waitingForApprovalCompletion = false;
    } finally {
      this.isLoading = false;
    }
  }

  /**
   * Override navigateBack to navigate to homepage with KRC20 tab selected
   */
  protected override navigateBack(): void {
    // Close the flow and navigate to homepage with KRC20 tab
    this.flowPagesService.closePage();
    this.router.navigate(['/app/home'], { queryParams: { tab: 'krc20' } });
  }
}
